import { assertDateString, DateString, urlDateFormat } from 'common/UrlDate';
import { first, flow, isString, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useReducer } from 'react';
import { useRouteMatch } from 'react-router';
import { ToastPosition } from 'react-toastify';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { useDishFistCartPositionContext } from 'src/pages/consumer/components/ConsumerHeaderWrapper';
import Notifications from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { useDispatch, useSelector } from 'src/store';
import { clearQueue, removeLineItem, setDeliveryDate } from 'src/store/cart/actions';
import { selectFullLineItems } from 'src/store/cart/selectors';
import { CartOrder } from 'src/store/cart/types';
import { match, P } from 'ts-pattern';
import { useIsDishFirst } from '../../../../../dish-first/useIsDishFirst';
import { AcknowledgeDeliveryDateChangeModal } from './AcknowledgeDeliveryDateChangeModal';
import { AcknowledgeStartNewOrderModal } from './AcknowledgeStartNewOrder';
import { EditOrderDeliveryModal } from './EditOrderModals';

enum ModalAction {
  CLOSE = 'close',
  EDIT_ORDER_DELIVERY = 'edit-order-delivery',
  ACKNOWLEDGE_DELIVERY_DATE_CHANGE = 'acknowledge-delivery-date-change',
  ACKNOWLEDGE_START_NEW_ORDER = 'acknowledge-start-new-order',
}

enum ModalState {
  CLOSED = 'closed',
  EDIT_ORDER_DELIVERY = 'edit-order-delivery',
  ACKNOWLEDGE_DELIVERY_DATE_CHANGE = 'acknowledge-delivery-date-change',
  ACKNOWLEDGE_START_NEW_ORDER = 'acknowledge-start-new-order',
}

const initialState: ModalState = ModalState.CLOSED;

const reducer = (state: ModalState, action: ModalAction): ModalState =>
  match([state, action])
    .with([ModalState.CLOSED, ModalAction.EDIT_ORDER_DELIVERY], () => ModalState.EDIT_ORDER_DELIVERY)
    .with(
      [ModalState.CLOSED, ModalAction.ACKNOWLEDGE_DELIVERY_DATE_CHANGE],
      () => ModalState.ACKNOWLEDGE_DELIVERY_DATE_CHANGE
    )
    .with([ModalState.CLOSED, ModalAction.ACKNOWLEDGE_START_NEW_ORDER], () => ModalState.ACKNOWLEDGE_START_NEW_ORDER)
    .with([P.any, ModalAction.CLOSE], () => ModalState.CLOSED)
    .otherwise(() => state);

export type HandleEditMultiOrderDeliveryDate = (payload: { shefIds: string[] }) => void;
export type HandleDeliveryDateChange = (payload: { shefIds: string[]; deliveryDateTime: DateTime }) => void;
export type HandleStartCheckout = () => void;

export const useMultiOrderCartModal = () => {
  const [state, modalDispatch] = useReducer(reducer, initialState);
  const [, setDeliveryDates] = usePersistentDeliveryDates();
  const dispatch = useDispatch();
  const cart = useSelector((state) => ({
    requireUserAcknowledgeDeliveryDateUpdate: state.cart.requireUserAcknowledgeDeliveryDateUpdate,
    queuedActions: state.cart.queuedActions,
    queuedActionsShefIds: uniq(state.cart.queuedActions.map((action) => action.payload.foodItem.userId)),
    shefIds: state.cart.shefs.ids.filter(isString),
    lineItems: selectFullLineItems(state.cart),
  }));

  useEffect(() => {
    if (cart.requireUserAcknowledgeDeliveryDateUpdate && state !== ModalState.ACKNOWLEDGE_DELIVERY_DATE_CHANGE) {
      modalDispatch(ModalAction.ACKNOWLEDGE_DELIVERY_DATE_CHANGE);
    }
  }, [cart.requireUserAcknowledgeDeliveryDateUpdate, state]);

  const hasActions = cart.queuedActions.length > 0;
  useEffect(() => {
    if (hasActions) {
      modalDispatch(ModalAction.ACKNOWLEDGE_START_NEW_ORDER);
    }
  }, [hasActions]);

  const handleEditMultiOrderDeliveryDate: HandleEditMultiOrderDeliveryDate = useCallback(
    () => modalDispatch(ModalAction.EDIT_ORDER_DELIVERY),
    [modalDispatch]
  );

  // HACK && FUTURE TODO: put this in a redux reducer... these changes require firing off a bunch of network
  // events to keep the server in sync, this allows us to get the feature done, but it's worth coming back to do correctly
  const handleAcceptStartNewOrder = () => {
    const { lineItems, queuedActions } = cart;
    const newDeliveryDate = first(queuedActions)?.payload.deliveryDate;
    const lineItemsToClear = lineItems.filter(
      ({ foodItem }) =>
        !foodItem.availability.find(
          ({ availabilityDate }) =>
            DateTime.fromJSDate(new Date(availabilityDate)).toFormat(urlDateFormat) === newDeliveryDate
        )
    );

    lineItemsToClear.forEach((item) =>
      dispatch(removeLineItem(false, { foodItem: item.foodItem, deliveryDate: '', location: 'other' }))
    );

    // only undefined if queuedActions is empty, which should never be the case
    if (newDeliveryDate) {
      setDeliveryDates([assertDateString(newDeliveryDate)]);
      dispatch(setDeliveryDate(false, newDeliveryDate));
    }
    // process stored actions and reset the queue
    queuedActions.forEach((action) => dispatch(action));

    dispatch(clearQueue());
  };

  return {
    state,
    dispatch: modalDispatch,
    handlers: {
      editMultiOrderDeliveryDate: handleEditMultiOrderDeliveryDate,
      acceptStartNewOrder: handleAcceptStartNewOrder,
    },
  };
};

export const ConsumerOrderMultiCartInfoModals = ({
  modalState,
  modalDispatch,
  newDeliveryDate,
  orders,
  onClearCart,
  onDeliveryDateChange,
  onCancelStartNewOrder,
  onAcceptStartNewOrder,
  onAcceptRefreshSearch,
  onAcknowledgeDeliveryDateChange,
}: {
  modalState: ModalState;
  modalDispatch: (action: ModalAction) => void;
  newDeliveryDate?: DateString;
  orders: CartOrder[];
  onClearCart: () => void;
  onCancelStartNewOrder: () => void;
  onAcceptStartNewOrder: () => void;
  onAcceptRefreshSearch: () => void;
  onDeliveryDateChange: HandleDeliveryDateChange;
  onAcknowledgeDeliveryDateChange: () => void;
}) => {
  const { isOpen: isCartOpenOnExplore } = useDishFistCartPositionContext();
  const isExplorePage = useRouteMatch(Routes.CONSUMER_EXPLORE);
  const { isDishFirst } = useIsDishFirst();
  const isCartShowing = isExplorePage && isDishFirst ? isCartOpenOnExplore : true;

  const onClose = () => modalDispatch(ModalAction.CLOSE);

  const displayToast = () => Notifications.success('Delivery date updated', { position: ToastPosition.BOTTOM_CENTER });

  return match(modalState)
    .with(ModalState.CLOSED, () => null)
    .with(ModalState.EDIT_ORDER_DELIVERY, () => (
      <EditOrderDeliveryModal onClose={onClose} onDeliveryDateChange={onDeliveryDateChange} />
    ))
    .with(ModalState.ACKNOWLEDGE_DELIVERY_DATE_CHANGE, () => (
      <AcknowledgeDeliveryDateChangeModal
        isShowing={isCartShowing}
        onAcknowledge={flow(onAcknowledgeDeliveryDateChange, onClose)}
        onClear={flow(onClearCart, onAcknowledgeDeliveryDateChange, onClose)}
      />
    ))
    .with(ModalState.ACKNOWLEDGE_START_NEW_ORDER, () => (
      <AcknowledgeStartNewOrderModal
        orders={orders}
        newDeliveryDate={newDeliveryDate}
        onCancel={flow(onCancelStartNewOrder, onClose)}
        onAccept={flow(onAcceptStartNewOrder, onClose, displayToast, onAcceptRefreshSearch)}
      />
    ))
    .exhaustive();
};
